import {
  Calendar,
  Stethoscope,
  Pill,
  FlaskConical,
  RefreshCw,
  FilePen,
  PencilLine,
  Receipt,
  type LucideIcon,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  INVOICE_SOURCE_COLORS,
  INVOICE_SOURCE_LABELS,
  getInvoiceSourceContext,
} from '@/lib/invoiceFormat'
import type { Invoice, InvoiceSource } from '@/services/invoicesService'

const SOURCE_ICONS: Record<InvoiceSource, LucideIcon> = {
  manual: PencilLine,
  auto_from_booking: Calendar,
  auto_from_encounter: Stethoscope,
  auto_from_prescription: Pill,
  auto_from_lab_order: FlaskConical,
  subscription_refill: RefreshCw,
  amendment_delta: FilePen,
}

const FALLBACK_COLORS = 'bg-slate-100 text-slate-700 border-slate-300'

const formatFallbackLabel = (source: string) =>
  source
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

interface Props {
  source: InvoiceSource | string
  invoice?: Invoice
  showContext?: boolean
  className?: string
}

export function InvoiceSourceBadge({
  source,
  invoice,
  showContext = true,
  className,
}: Props) {
  const Icon = SOURCE_ICONS[source as InvoiceSource] ?? Receipt
  const label =
    INVOICE_SOURCE_LABELS[source as InvoiceSource] ?? formatFallbackLabel(source)
  const colorClasses =
    INVOICE_SOURCE_COLORS[source as InvoiceSource] ?? FALLBACK_COLORS
  const ctx = showContext && invoice ? getInvoiceSourceContext(invoice) : null

  return (
    <div className={cn('flex items-center gap-2 min-w-0', className)}>
      <Badge
        className={cn(
          'inline-flex shrink-0 items-center gap-1 font-medium',
          colorClasses,
        )}
      >
        <Icon className="h-3 w-3" aria-hidden />
        {label}
      </Badge>
      {ctx ? (
        <span className="text-xs text-muted-foreground truncate">{ctx}</span>
      ) : null}
    </div>
  )
}
